/* eslint-disable @next/next/no-img-element */
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { FC, ReactNode } from 'react'
import type { SupportArticle } from '@/data/support'

import Link from 'next/link'

import { Layout, Seo, PageHero } from '@/components/generals'
import { SupportNav } from '@/components/support'
import { routes } from '@/lib/routes'
import { getSupportArticles, getSupportBySlug } from '@/data/support'

type SupportArticlePageProps = {
    article: SupportArticle
    articles: SupportArticle[]
}

const SupportArticlePage: FC<SupportArticlePageProps> = ({ article, articles }): ReactNode => {
    return (
        <Layout>
            <Seo title={article.name} description={`${article.name} — San Diego support.`} />

            <PageHero badge='All' eyebrow='Support tickets' eyebrowHref={routes.support} title={article.name} />

            <div className='section'>
                <div className='container'>
                    <div className='support-grid-layout animation-on-page-load'>
                        <SupportNav articles={articles} activeSlug={article.slug} id='w-node-_7991320a-a1b5-78af-97ed-275d7a66a8d7-77af589f' />

                        <div id='w-node-_7991320a-a1b5-78af-97ed-275d7a66a8e6-77af589f' className='support-article-holder'>
                            <img
                                src='/images/Logo-About.png'
                                loading='lazy'
                                sizes='100px'
                                srcSet='/images/Logo-About-p-500.png 500w, /images/Logo-About-p-800.png 800w, /images/Logo-About.png 956w'
                                alt='San Diego'
                                className='support-app-icon small'
                            />
                            <div className='support-content w-richtext' dangerouslySetInnerHTML={{ __html: article.content }} />
                        </div>

                        <div id='w-node-_7991320a-a1b5-78af-97ed-275d7a66a8f7-77af589f' className='support-cta-holder'>
                            <h2 className='card-title-sm'>Not finding what you&apos;re looking for ?</h2>
                            <p className='grey-text'>Our team is here to help. Reach out and we will get back to you quickly.</p>
                            <Link href={routes.contact} className='button w-button'>
                                Contact us
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    const articles = await getSupportArticles()
    return {
        paths: articles.map(article => ({ params: { slug: article.slug } })),
        fallback: 'blocking'
    }
}

export const getStaticProps: GetStaticProps<SupportArticlePageProps> = async ({ params }) => {
    const slug = params?.slug as string
    const [article, articles] = await Promise.all([getSupportBySlug(slug), getSupportArticles()])

    if (!article) {
        return { notFound: true }
    }

    return { props: { article, articles }, revalidate: 60 }
}

export default SupportArticlePage
